import type {
  LocalColumnFilter,
  LocalColumnFiltersState,
} from "@/shared/types/datatable.types";
import { SortDirection } from "@spz/shared-domain";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

export function extractMinMaxFromArray(values: unknown): {
  min?: number;
  max?: number;
} {
  if (typeof values !== "string") {
    return { min: undefined, max: undefined };
  }

  const [extractedMin, extractedMax] = values.split(",");

  const min = extractedMin ? Number.parseInt(extractedMin) : undefined;
  const max = extractedMax ? Number.parseInt(extractedMax) : undefined;

  return { min, max };
}

export const objectToColumnFilters = (
  obj: Record<string, string>,
  {
    numericColumns,
    rangeColumns,
  }: { numericColumns: string[]; rangeColumns: string[] } = {
    numericColumns: [],
    rangeColumns: [],
  },
): LocalColumnFiltersState => {
  return Object.entries(obj)
    .filter(([_, value]) => value && value !== "," && value !== "")
    .map(([key, value]) => {
      if (rangeColumns.includes(key)) {
        let valueArr: unknown[];
        if (value.includes(",")) {
          valueArr = value.split(",").map((i) => (i === "" ? undefined : i));
        } else {
          valueArr = [value, undefined];
        }

        return {
          id: key,
          value: valueArr,
        };
      }

      return {
        id: key,
        value,
      };
    })
    .map((filter) => {
      if (numericColumns.includes(filter.id)) {
        if (Array.isArray(filter.value)) {
          return {
            ...filter,
            value: filter.value.map((i) => {
              if (i === "") {
                return undefined;
              }
              if (typeof i === "string") {
                return Number(i);
              }
              return undefined;
            }),
          };
        }

        return {
          ...filter,
          value: filter.value ? Number(filter.value) : undefined,
        };
      }

      return filter;
    });
};

const columnFilterIsEmpty = (
  colFilter: LocalColumnFilter<unknown> | undefined,
) => {
  if (!colFilter || !colFilter.value) {
    return true;
  }

  if (Array.isArray(colFilter.value)) {
    const isEmptyArray = colFilter.value.every(
      (i) => i === "" || i === null || i === undefined,
    );
    if (isEmptyArray) {
      return true;
    }
  }

  return false;
};

/**
 * Loop on each columns keys to force empty string if filter undefined
 * Set empty string will remove filter from url cf. nuqs > clearOnDefault
 * @param param0
 * @returns
 */
export const columnFiltersToObject = ({
  columnKeys,
  columnFilters,
}: {
  columnKeys: string[];
  columnFilters: LocalColumnFiltersState;
}): Record<string, string> => {
  const _result = columnKeys.map((key) => {
    const colFilter = columnFilters.find((f) => f.id === key);
    const isEmptyFilter = columnFilterIsEmpty(colFilter);

    // Check empty filter
    if (!colFilter || isEmptyFilter) {
      return [key, ""];
    }

    // stringify value
    return [key, String(colFilter.value)];
  });
  return Object.fromEntries(_result);
};

/**
 * Reduce column filters
 * @param value
 * @returns
 */
export function reduceColumnFilters(
  value: ColumnFiltersState,
): ColumnFiltersState {
  return value.reduce(
    (previous, current) => {
      if (!current || !current.id) {
        return previous;
      }

      // Remove any existing filter with the same id
      const filtered = previous.filter((filter) => filter.id !== current.id);

      // Only add if value is valid (not empty/null)
      if (Array.isArray(current.value)) {
        if (
          current.value.some((value) => value !== null && value !== undefined)
        ) {
          return [...filtered, current];
        }

        return filtered;
      }

      return [...filtered, current];
    },
    [] as typeof value,
  );
}

/**
 * Get updated value by updated / old state
 * @param param0
 * @returns
 */
export const getUpdaterOrValue = <T>({
  updaterOrValue,
  old,
}: {
  updaterOrValue: T | ((old: T) => T);
  old: T;
}): T =>
  typeof updaterOrValue === "function"
    ? (updaterOrValue as (old: T) => T)(old)
    : updaterOrValue;

/**
 * Reduce sorting
 * @param value
 * @returns
 */
export function reduceSorting(value: SortingState): SortingState {
  return value.reduce(
    (previous, current) => {
      if (!current || !current.id) {
        return previous;
      }

      // Remove any existing filter with the same id
      const filtered = previous.filter((filter) => filter.id !== current.id);

      return [...filtered, current];
    },
    [] as typeof value,
  );
}

export const mapSortingToRequestDto = <T = string>(
  value: SortingState,
): { sortField: T; sortDirection: SortDirection }[] => {
  return value.map((item) => {
    return {
      sortField: item.id as T,
      sortDirection: item.desc ? SortDirection.DESC : SortDirection.ASC,
    };
  });
};
